import { createFileRoute } from "@tanstack/react-router";

/** Utility components */
import { MailIcon } from "lucide-react";

export const Route = createFileRoute("/_app/api-agent-doc")({
  component: RouteComponent,
});

const Section: React.FC<{
  id?: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, title, children }) => (
  <section id={id} className="mb-12 scroll-mt-20">
    <h2 className="mb-6 border-b-2 border-blue-200 pb-3 text-3xl font-bold text-gray-900 dark:border-blue-800 dark:text-white">
      {title}
    </h2>
    {children}
  </section>
);

const SubSection: React.FC<{
  id?: string;
  title: string;
  children: React.ReactNode;
  level?: number;
}> = ({ id, title, children, level = 3 }) => (
  <div id={id} className="mb-8 scroll-mt-16">
    <h3
      className={`mb-4 font-bold text-gray-800 dark:text-gray-200 ${
        level === 3 ? "text-xl" : "text-lg"
      }`}
    >
      {title}
    </h3>
    {children}
  </div>
);

const CodeBlock: React.FC<{
  children: React.ReactNode;
  language?: string;
}> = ({ children, language }) => (
  <div className="mb-4">
    {language && (
      <div className="rounded-t border-b border-gray-700 bg-gray-800 px-3 py-1 font-mono text-xs text-gray-200">
        {language}
      </div>
    )}
    <pre
      className={`overflow-x-auto rounded ${
        language ? "rounded-t-none" : ""
      } border border-gray-200 bg-gray-50 p-4 font-mono text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200`}
    >
      {children}
    </pre>
  </div>
);

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="rounded border border-gray-300 bg-gray-200 px-2 py-1 font-mono text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
    {children}
  </code>
);

const EndpointCard: React.FC<{
  method: string;
  url: string;
  children: React.ReactNode;
}> = ({ method, url, children }) => (
  <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div className="mb-4 flex items-center gap-3">
      <span
        className={`rounded-full px-3 py-1 text-xs font-bold text-white ${
          method === "GET"
            ? "bg-green-500"
            : method === "POST"
              ? "bg-blue-500"
              : method === "PUT"
                ? "bg-yellow-500"
                : method === "DELETE"
                  ? "bg-red-500"
                  : "bg-gray-500"
        }`}
      >
        {method}
      </span>
      <code className="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-700 dark:bg-gray-700 dark:text-gray-300">
        {url}
      </code>
    </div>
    {children}
  </div>
);

const ResponseExample: React.FC<{
  status: string;
  response: string;
  isSuccess?: boolean;
}> = ({ status, response, isSuccess = false }) => (
  <div className="mb-4">
    <div className="mb-2 flex items-center gap-2">
      <span
        className={`w-16 rounded py-1 text-center text-xs font-bold ${
          isSuccess
            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
        }`}
      >
        {status}
      </span>
      <span className="text-sm text-gray-600 dark:text-gray-400">Response</span>
    </div>
    <CodeBlock language="json">{response}</CodeBlock>
  </div>
);

function RouteComponent() {
  return (
    <div className="min-h-screen">
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Title */}
        <header className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold text-gray-900 dark:text-white">
            API Documentation
          </h1>
        </header>

        {/* Quick TOC */}
        <nav
          aria-label="Table of contents"
          className="mb-12 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
        >
          <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
            Contents
          </h2>
          <ul className="columns-3 gap-8 space-y-2 text-sm">
            <li>
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#introduction"
              >
                1. Introduction
              </a>
            </li>
            <li>
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#authentication"
              >
                2. Authentication
              </a>
            </li>
            <li>
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#base-url"
              >
                3. Base URL
              </a>
            </li>
            <li className="break-before-column">
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#endpoints"
              >
                4. Endpoints
              </a>
            </li>
            <li className="pl-4">
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#pii-text-detect"
              >
                4.1. PII Text Detect
              </a>
            </li>
            <li className="pl-4">
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#advanced-pii-text-detect"
              >
                4.2. Advanced PII Text Detect
              </a>
            </li>
            <li className="pl-4">
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#full-list-of-entities"
              >
                4.3. Full List of Entities
              </a>
            </li>
            <li className="pl-4">
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#file-redact"
              >
                4.4. File Redact
              </a>
            </li>
            <li className="pl-4">
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#advanced-file-redact"
              >
                4.5. Advanced File Redact
              </a>
            </li>
            <li className="pl-4">
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#supported-file-types"
              >
                4.6. Supported File Types
              </a>
            </li>
            <li className="break-before-column">
              <a
                className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                href="#support-contact"
              >
                5. Support Contact
              </a>
            </li>
          </ul>
        </nav>

        {/* 1. Introduction */}
        <Section id="introduction" title="1. Introduction">
          <div className="mb-6 rounded-r-lg border-l-4 border-blue-400 bg-blue-50 p-6 dark:bg-blue-900/20">
            <p className="mb-3 text-gray-700 dark:text-gray-300">
              The Polygraf Governance API allows users to interact with various
              endpoints that provide services related to Personally Identifiable
              Information (PII) detection and redaction. This API requires
              authentication in the form of an API key.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              This document outlines the API's authentication process, available
              endpoints, and request and response structures, to help developers
              effectively integrate its capabilities into their applications.
            </p>
          </div>
        </Section>

        {/* 2. Authentication */}
        <Section id="authentication" title="2. Authentication">
          <div className="mb-6 rounded-r-lg border-l-4 border-amber-400 bg-amber-50 p-6 dark:bg-amber-900/20">
            <p className="mb-3 text-gray-700 dark:text-gray-300">
              The API uses an API Key for authentication, which must be included
              in the request header as the <Kbd>API_Key</Kbd> key. The server
              will validate the API Key with each request to authorize access.
            </p>
            <p className="mb-2 text-gray-700 dark:text-gray-300">
              Include the API Key in the request header as follows:
            </p>
            <ul className="mb-3 ml-6 list-disc text-gray-700 dark:text-gray-300">
              <li>
                Header: <Kbd>API_Key</Kbd>
              </li>
              <li className="pt-2">
                Value: <Kbd>Bearer {"{YOUR API KEY}"}</Kbd>
              </li>
            </ul>
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-800 dark:text-red-200">
                Your API Key is unique to your account. Sharing it with others
                could compromise your access.
              </p>
              <p className="mt-2 text-sm text-red-800 dark:text-red-200">
                Treat the API Key like a password. Avoid exposing it in public
                repositories, client-side code, or unprotected environments.
              </p>
            </div>
          </div>
        </Section>

        {/* 3. Base URL */}
        <Section id="base-url" title="3. Base URL">
          <div className="mb-6 rounded-r-lg border-l-4 border-green-400 bg-green-50 p-6 dark:bg-green-900/20">
            <p className="mb-3 text-gray-700 dark:text-gray-300">
              The Base URL serves as the root address for all API requests. To
              make a request to any endpoint, clients will append the endpoint
              path to this Base URL.
            </p>
            <CodeBlock language="bash">https://api.polygraf.ai/v1</CodeBlock>
          </div>
        </Section>

        {/* 4. Endpoints */}
        <Section id="endpoints" title="4. Endpoints">
          {/* 4.1 PII Text Detect */}
          <SubSection id="pii-text-detect" title="4.1. PII Text Detect">
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Essential privacy protection for standard use, with the ability to
              detect and redact over 35 types of PII, PCI, PHI, and
              SPI—including names, emails, phone numbers, and other sensitive
              identifiers.
            </p>

            <EndpointCard method="POST" url="/pii/text-detect">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to analyze for PII"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`[
  {
    "entity_type": "PERSON",
    "entity_text": "John Doe",
    "start": 45,
    "end": 53,
    "score": 0.98
  }
]`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"Your account is currently restricted due to an outstanding payment. Please settle your dues to regain access to this API."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeBlock language="bash">{`curl -X POST 'https://api.polygraf.ai/v1/pii/text-detect' \\
-H 'Content-Type: application/json' \\
-H 'API_Key: Bearer <API-Key>' \\
-d '{
  "text": "This is for PII detection. It can contain names like John Doe, phone numbers like 555-123-4567, or email addresses like user@example.com."
}'`}</CodeBlock>
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.2 Advanced PII Text Detect */}
          <SubSection
            id="advanced-pii-text-detect"
            title="4.2. Advanced PII Text Detect"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Comprehensive privacy protection, securing PII, PCI, PHI, and SPI
              along with biometric, financial, and geolocation data. Includes
              contextual detection and customizable redaction rules.
            </p>

            <EndpointCard method="POST" url="/pii/advanced-text-detect">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Body
                  </h4>
                  <CodeBlock language="json">{`{
  "text": "string - The text content to analyze for advanced PII detection"
}`}</CodeBlock>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`[
  {
    "entity_type": "PERSON",
    "entity_text": "John Doe",
    "start": 45,
    "end": 53,
    "score": 0.98
  }
]`}
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"Your account is currently restricted due to an outstanding payment. Please settle your dues to regain access to this API."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeBlock language="bash">{`curl -X POST 'https://api.polygraf.ai/v1/pii/advanced-text-detect' \\
-H 'Content-Type: application/json' \\
-H 'API_Key: Bearer <API-Key>' \\
-d '{
  "text": "This is for PII detection. It can contain names like John Doe, phone numbers like 555-123-4567, or email addresses like user@example.com."
}'`}</CodeBlock>
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.3 Full List of Entities */}
          <SubSection
            id="full-list-of-entities"
            title="4.3. Full List of Entities"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Returns the full list of supported entities.
            </p>

            <EndpointCard method="GET" url="/pii/full_entity_list">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`[
  "PERSON",
  "EMAIL",
  "PHONE_NUMBER",
  "CREDIT_CARD",
  "SSN",
  "ADDRESS"
]`}
                    isSuccess={true}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeBlock language="bash">{`curl 'https://api.polygraf.ai/v1/pii/full_entity_list' \\
-H 'Content-Type: application/json'`}</CodeBlock>
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.4 File Redact */}
          <SubSection id="file-redact" title="4.4. File Redact">
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Processes the input file and returns the binary data of the
              redacted document. Automatically mask sensitive data in documents,
              reports, and filesystems.
            </p>

            <EndpointCard method="POST" url="/pii/file-redact">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>multipart/form-data</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Form Data
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="font-semibold">Field</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>file</Kbd>
                      </div>
                      <div>Path to input file</div>
                      <div>
                        <Kbd>entity_list</Kbd>
                      </div>
                      <div>Array of entity types to detect</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response="bytestream"
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"Your account is currently restricted due to an outstanding payment. Please settle your dues to regain access to this API."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeBlock language="bash">{`curl -X POST 'https://api.polygraf.ai/v1/pii/file-redact' \\
-H 'API_Key: Bearer <API-Key>' \\
-F 'file=@"path/to/your/file"' \\
-F 'entity_list="["entity1", "entity2"]"'`}</CodeBlock>
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.5 Advanced File Redact */}
          <SubSection
            id="advanced-file-redact"
            title="4.5. Advanced File Redact"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Processes the input file and returns the binary data of the
              redacted document. Scans and sanitizes Word docs, PDFs, and other
              files. Instantly redacts PII, PCI, PHI and other SI.
            </p>

            <EndpointCard method="POST" url="/pii/advanced-file-redact">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>API_Key</Kbd>
                      </div>
                      <div>
                        <Kbd>Bearer {"{api-key}"}</Kbd>
                      </div>
                      <div>Authentication token</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>multipart/form-data</Kbd>
                      </div>
                      <div>Request format</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Form Data
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="font-semibold">Field</div>
                      <div className="font-semibold">Description</div>
                      <div>
                        <Kbd>file</Kbd>
                      </div>
                      <div>Path to input file</div>
                      <div>
                        <Kbd>entity_list</Kbd>
                      </div>
                      <div>Array of entity types to detect</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response="bytestream"
                    isSuccess={true}
                  />
                  <ResponseExample
                    status="401"
                    response={`{"detail":"Invalid API Key."}`}
                  />
                  <ResponseExample
                    status="403"
                    response={`{"detail":"Your account is currently restricted due to an outstanding payment. Please settle your dues to regain access to this API."}`}
                  />
                  <ResponseExample
                    status="422"
                    response={`{"detail":"Validation error description"}`}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeBlock language="bash">{`curl -X POST 'https://api.polygraf.ai/v1/pii/advanced-file-redact' \\
-H 'API_Key: Bearer <API-Key>' \\
-F 'file=@"path/to/your/file"' \\
-F 'entity_list="["entity1", "entity2"]"'`}</CodeBlock>
                </div>
              </div>
            </EndpointCard>
          </SubSection>

          {/* 4.6 Supported File Types */}
          <SubSection
            id="supported-file-types"
            title="4.6. Supported File Types"
          >
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Returns the supported file types and their extensions.
            </p>

            <EndpointCard method="GET" url="/pii/supported-file-types">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Request Headers
                  </h4>
                  <div className="rounded bg-gray-50 p-3 dark:bg-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="font-semibold">Header</div>
                      <div className="font-semibold">Value</div>
                      <div>
                        <Kbd>Content-Type</Kbd>
                      </div>
                      <div>
                        <Kbd>application/json</Kbd>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Response Examples
                  </h4>
                  <ResponseExample
                    status="200"
                    response={`{
  "Document": ["pdf", "doc", "docx"],
  "Image": ["jpg", "png", "gif"],
  "Text": ["txt", "rtf", "md"]
}`}
                    isSuccess={true}
                  />
                </div>

                <div>
                  <h4 className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Example Request
                  </h4>
                  <CodeBlock language="bash">{`curl 'https://api.polygraf.ai/v1/pii/supported-file-types' \\
-H 'Content-Type: application/json'`}</CodeBlock>
                </div>
              </div>
            </EndpointCard>
          </SubSection>
        </Section>

        {/* 5. Support Contact */}
        <Section id="support-contact" title="5. Support Contact">
          <div className="rounded-r-lg border-l-4 border-blue-400 bg-blue-50 p-6 dark:bg-blue-900/20">
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              For further help, contact:
            </p>
            <div className="rounded-lg border border-blue-200 bg-white p-4 dark:border-blue-800 dark:bg-gray-800">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                  <MailIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <a
                    className="text-blue-600 transition-colors hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    href="mailto:vali@polygraf.ai"
                  >
                    vali@polygraf.ai
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
